import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { CalendarIcon, TrendingUp, BarChart3, PieChartIcon, FileDown, FileSpreadsheet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { notificationAnalyticsService, DailyNotificationStats } from '@/services/notificationAnalyticsService';
import { pdfExportService } from '@/services/pdfExportService';
import { csvExportService } from '@/services/csvExportService';
import { toast } from 'sonner';

type DateRange = {
  from: Date;
  to: Date;
};

type ChartType = 'line' | 'bar' | 'pie';

export const NotificationAnalyticsChart = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [chartType, setChartType] = useState<ChartType>('line');
  const [entityType, setEntityType] = useState<'team' | 'venue' | 'coach' | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dailyStats, setDailyStats] = useState<DailyNotificationStats[]>([]);
  const [summary, setSummary] = useState({
    totalNotifications: 0,
    totalSent: 0,
    totalFailed: 0,
    totalPending: 0,
    overallSuccessRate: 0,
    averagePerDay: 0
  });
  const [entityBreakdown, setEntityBreakdown] = useState<any[]>([]);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, entityType]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const trends = await notificationAnalyticsService.getNotificationTrends(
        dateRange.from,
        dateRange.to,
        entityType
      );

      setDailyStats(trends.daily);
      setSummary(trends.summary);

      // Load entity breakdown
      const breakdown = await notificationAnalyticsService.getEntityTypeBreakdown(
        dateRange.from,
        dateRange.to
      );
      setEntityBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (field: 'from' | 'to') => (date: Date | undefined) => {
    if (date) {
      setDateRange(prev => ({ ...prev, [field]: date }));
    }
  };

  const setQuickRange = (days: number) => {
    setDateRange({
      from: subDays(new Date(), days),
      to: new Date()
    });
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await pdfExportService.exportAnalyticsToPDF({
        title: 'Notification Analytics Report',
        dateRange,
        summary,
        entityBreakdown,
        chartElementId: 'analytics-chart-container'
      });
      toast.success('PDF report exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => {
    try {
      csvExportService.exportCompleteAnalytics(
        dailyStats,
        summary,
        entityBreakdown,
        dateRange,
        entityType
      );
      toast.success('CSV data exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV data');
    }
  };

  const handleExportDailyCSV = () => {
    try {
      csvExportService.exportDailyAnalytics(dailyStats, dateRange, entityType);
      toast.success('Daily analytics exported to CSV');
    } catch (error) {
      console.error('Error exporting daily CSV:', error);
      toast.error('Failed to export daily analytics');
    }
  };

  const handleExportSummaryCSV = () => {
    try {
      csvExportService.exportSummaryStats(summary, entityBreakdown, dateRange);
      toast.success('Summary statistics exported to CSV');
    } catch (error) {
      console.error('Error exporting summary CSV:', error);
      toast.error('Failed to export summary statistics');
    }
  };

  const COLORS = {
    sent: '#10b981',
    failed: '#ef4444',
    pending: '#f59e0b',
    total: '#3b82f6'
  };

  const PIE_COLORS = ['#10b981', '#ef4444', '#f59e0b'];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Total Notifications</p>
              <p className="text-3xl font-bold">{summary.totalNotifications}</p>
              <p className="text-xs text-muted-foreground">
                {summary.averagePerDay.toFixed(1)} per day
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                {summary.overallSuccessRate.toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.totalSent} sent successfully
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Failed</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                {summary.totalFailed}
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.totalNotifications > 0 
                  ? ((summary.totalFailed / summary.totalNotifications) * 100).toFixed(1)
                  : 0}% failure rate
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                {summary.totalPending}
              </p>
              <p className="text-xs text-muted-foreground">
                Awaiting delivery
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Delivery Trends
              </CardTitle>
              <CardDescription>
                Interactive analytics for notification delivery performance
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline"
                    disabled={loading}
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Choose Export Type</h4>
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="ghost" 
                        className="justify-start" 
                        size="sm"
                        onClick={handleExportCSV}
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Complete Analytics
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="justify-start" 
                        size="sm"
                        onClick={handleExportDailyCSV}
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Daily Statistics Only
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="justify-start" 
                        size="sm"
                        onClick={handleExportSummaryCSV}
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Summary Only
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      CSV files can be opened in Excel, Google Sheets, or any spreadsheet application
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button 
                onClick={handleExportPDF} 
                disabled={exporting || loading}
                variant="outline"
              >
                <FileDown className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export PDF'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Range Selection */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !dateRange.from && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? format(dateRange.from, "PPP") : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange.from}
                    onSelect={handleDateSelect('from')}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>

              <span className="text-muted-foreground">to</span>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !dateRange.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.to ? format(dateRange.to, "PPP") : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange.to}
                    onSelect={handleDateSelect('to')}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Quick Range Buttons */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setQuickRange(7)}>
                Last 7 days
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange(30)}>
                Last 30 days
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange(90)}>
                Last 90 days
              </Button>
            </div>

            {/* Entity Type Filter */}
            <Select value={entityType} onValueChange={(value: any) => setEntityType(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="team">Teams</SelectItem>
                <SelectItem value="venue">Venues</SelectItem>
                <SelectItem value="coach">Coaches</SelectItem>
              </SelectContent>
            </Select>

            {/* Chart Type Selector */}
            <div className="flex gap-2 ml-auto">
              <Button
                variant={chartType === 'line' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('line')}
              >
                <TrendingUp className="h-4 w-4" />
              </Button>
              <Button
                variant={chartType === 'bar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('bar')}
              >
                <BarChart3 className="h-4 w-4" />
              </Button>
              <Button
                variant={chartType === 'pie' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartType('pie')}
              >
                <PieChartIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Chart */}
          <div id="analytics-chart-container" ref={chartRef}>
            {loading ? (
              <div className="h-[400px] flex items-center justify-center">
                <p className="text-muted-foreground">Loading analytics...</p>
              </div>
            ) : dailyStats.length === 0 ? (
              <div className="h-[400px] flex items-center justify-center">
                <p className="text-muted-foreground">No data available for selected period</p>
              </div>
            ) : (
              <>
                {chartType === 'line' && (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={dailyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), 'MMM d')}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(date) => format(new Date(date), 'PPP')}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="sent" 
                        stroke={COLORS.sent} 
                        strokeWidth={2}
                        name="Sent"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="failed" 
                        stroke={COLORS.failed} 
                        strokeWidth={2}
                        name="Failed"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="pending" 
                        stroke={COLORS.pending} 
                        strokeWidth={2}
                        name="Pending"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {chartType === 'bar' && (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={dailyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), 'MMM d')}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(date) => format(new Date(date), 'PPP')}
                      />
                      <Legend />
                      <Bar dataKey="sent" fill={COLORS.sent} name="Sent" />
                      <Bar dataKey="failed" fill={COLORS.failed} name="Failed" />
                      <Bar dataKey="pending" fill={COLORS.pending} name="Pending" />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {chartType === 'pie' && entityBreakdown.length > 0 && (
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={entityBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.entityType}: ${entry.count}`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {entityBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </>
            )}
          </div>

          {/* Entity Breakdown Table */}
          {entityBreakdown.length > 0 && (
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Breakdown by Entity Type</h4>
              <div className="space-y-2">
                {entityBreakdown.map((entity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                      />
                      <span className="font-medium">{entity.entityType}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">{entity.count} notifications</Badge>
                      <Badge 
                        className={cn(
                          "min-w-[80px] justify-center",
                          entity.successRate >= 90 ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" :
                          entity.successRate >= 70 ? "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20" :
                          "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                        )}
                      >
                        {entity.successRate.toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
